import { CommandItem } from "@volca/ui/components/ui/command";
import { useGetBusiness } from "@/business/query";
import { getErrorMessage } from "@/core/api/errors";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import {
  FOX_SONS_BUSINESS_NAME,
  getFoxSonsVisibleTechIds,
} from "@/icm/constants/fox-sons-access-control";
import { useGetIcmStatements } from "@/icm/query";
import { useGetAiScriptSpreadsheet } from "@/payroll/ai-script/query/workbook";
import {
  getPayBreakdownPath,
  getPayBreakdownSheetPathForName,
  getPayBreakdownStatementPath,
} from "@/payroll/pay-breakdown/routes";
import {
  PAY_BREAKDOWN_SECTION,
  type PayBreakdownSection,
} from "@/payroll/pay-breakdown/types";
import { usePayrollSectionAccess } from "@/payroll/pay-breakdown/use-payroll-section-access";
import { CommandNestedState } from "./nested-list";
import type { CommandPage } from "./pages";

const SECTION_LINKS: {
  section: PayBreakdownSection;
  label: string;
  value: string;
}[] = [
  { section: PAY_BREAKDOWN_SECTION.roster, label: "Roster", value: "Roster" },
  {
    section: PAY_BREAKDOWN_SECTION.payPlan,
    label: "Plan",
    value: "Plan pay plan",
  },
  {
    section: PAY_BREAKDOWN_SECTION.adjustments,
    label: "PushPay",
    value: "PushPay adjustments",
  },
  {
    section: PAY_BREAKDOWN_SECTION.packs,
    label: "PayPacks",
    value: "PayPacks packs",
  },
  {
    section: PAY_BREAKDOWN_SECTION.packChat,
    label: "PackChat",
    value: "PackChat",
  },
];

export const PayrollPeriodPage = ({
  start,
  label,
  aiRunId,
  icmRunId,
  onOpen,
  onNavigate,
}: {
  start: string;
  label: string;
  aiRunId: string | null;
  icmRunId: string | null;
  onOpen: (page: CommandPage) => void;
  onNavigate: (to: string) => void;
}) => {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const business = useGetBusiness(businessId);
  const access = usePayrollSectionAccess({
    business: business.data,
    user,
    isAiScript: !!aiRunId,
  });
  const sectionsReady = !business.isLoading && !access.isLoading;

  return (
    <CommandNestedState
      heading={label}
      loading={false}
      error={null}
      empty="Nothing to open in this period."
      count={1}
    >
      <CommandItem
        value={`${label} open grid`}
        onSelect={() => onNavigate(getPayBreakdownPath(start))}
      >
        <span>Open this period</span>
      </CommandItem>
      {sectionsReady &&
        SECTION_LINKS.filter((link) => access.availability[link.section]).map(
          (link) => (
            <CommandItem
              key={link.section}
              value={`${label} ${link.value}`}
              onSelect={() =>
                onNavigate(getPayBreakdownPath(start, link.section))
              }
            >
              <span>{link.label}</span>
            </CommandItem>
          ),
        )}
      {aiRunId && (
        <CommandItem
          value={`${label} sheets`}
          onSelect={() =>
            onOpen({ kind: "payroll-sheets", start, label, aiRunId })
          }
        >
          <span>Sheets</span>
        </CommandItem>
      )}
      {icmRunId && (
        <CommandItem
          value={`${label} statements`}
          onSelect={() =>
            onOpen({ kind: "payroll-statements", start, label, icmRunId })
          }
        >
          <span>Statements</span>
        </CommandItem>
      )}
    </CommandNestedState>
  );
};

export const PayrollSheetsPage = ({
  start,
  aiRunId,
  onNavigate,
}: {
  start: string;
  aiRunId: string;
  onNavigate: (to: string) => void;
}) => {
  const { businessId } = useBusiness();
  const workbook = useGetAiScriptSpreadsheet(businessId, aiRunId);
  const sheets = workbook.data?.spreadsheet.sheets ?? [];

  return (
    <CommandNestedState
      heading="Sheets"
      loading={workbook.isLoading}
      error={
        workbook.isError
          ? `Couldn't load sheets. ${getErrorMessage(workbook.error)}`
          : null
      }
      onRetry={() => void workbook.refetch()}
      empty="No sheets."
      count={sheets.length}
    >
      {sheets.map((sheet) => (
        <CommandItem
          key={sheet.id}
          value={sheet.name}
          onSelect={() =>
            onNavigate(getPayBreakdownSheetPathForName(start, sheet.name))
          }
        >
          <span>{sheet.name}</span>
        </CommandItem>
      ))}
    </CommandNestedState>
  );
};

export const PayrollStatementsPage = ({
  start,
  icmRunId,
  onNavigate,
}: {
  start: string;
  icmRunId: string;
  onNavigate: (to: string) => void;
}) => {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const business = useGetBusiness(businessId);
  const statements = useGetIcmStatements(businessId, icmRunId);
  const visibleIds =
    business.data?.name === FOX_SONS_BUSINESS_NAME
      ? getFoxSonsVisibleTechIds(user?.email ?? "")
      : null;
  const rows = (statements.data ?? []).filter(
    (statement) =>
      !visibleIds ||
      (statement.technician_crm_external_id != null &&
        visibleIds.has(statement.technician_crm_external_id)),
  );

  return (
    <CommandNestedState
      heading="Statements"
      loading={statements.isLoading}
      error={
        statements.isError
          ? `Couldn't load statements. ${getErrorMessage(statements.error)}`
          : null
      }
      onRetry={() => void statements.refetch()}
      empty="No statements."
      count={rows.length}
    >
      {rows.map((statement) => {
        const label = statement.technician_name ?? "Statement";
        return (
          <CommandItem
            key={statement.id}
            value={`${label} ${statement.id}`}
            onSelect={() =>
              onNavigate(getPayBreakdownStatementPath(start, statement.id))
            }
          >
            <span>{label}</span>
          </CommandItem>
        );
      })}
    </CommandNestedState>
  );
};
